import { GrayCardWrapper, Wrapper } from "./reviewAndCheckout.style";
import { Button, CardText, Col, Container, Row } from "reactstrap";
import deliveryIcon from "../../../../../../assets/icons/delivery.png";
import { IoLocationOutline } from "react-icons/io5";
import { CiCircleInfo } from "react-icons/ci";
import { RiArrowDropRightLine } from "react-icons/ri";
import { OrderSummaryStyle } from "../../../shoppingCart/section/cart/cart.style";
import { AuthCustomModalDescriptionStyleB } from "../../../../../../layouts/auth/auth.layout.style";
import { CartItemCardComponent } from "../../../../../../components/cartItemCard/cartItemCard.component";
import {memo, useCallback, useEffect, useMemo, useState} from "react";
import { AddressManagementModalComponent } from "../../../../../../components/addressManagement";
import { useLocation, useNavigate } from "react-router";
import { useAddress } from "../../../../../../components/addressManagement/addressManagement";
import { activateNotification } from "../../../../../../utils/notifications-utils";
import { useAuth } from "../../../../../../providers/authentication.provider";
import { PageLoader } from "../../../../../../components/loaders";
import { useCart } from "../../../../../../providers/cart.provider";
import { NumericFormat } from "react-number-format";
import { AddDiscountCodeModalSection } from "../addDiscountCodeModal/addDiscountCodeModal.section";
import { Mixpanel } from "../../../../../../utils/mixpanel";
import { CustomHelper } from "../../../../../../helpers/custom-helper";
import {IoIosArrowBack} from "react-icons/io";

const LoadingOverlay = () => (
    <div
        role="alert"
        aria-busy="true"
        className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
        style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 10500000 }}
    >
      <PageLoader />
    </div>
);

export const ReviewAndCheckoutSection = memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAddressModalOpen, setIsAddressModalOpen, toggleAddressModal: toggleAuthenticatedUserAddressModal } = useAddress();
  const { user, isGuestUser, isAuthenticated } = useAuth();
  const { handleCheckout, cartItems } = useCart();

  const [checkoutData, setCheckoutData] = useState(location.state || null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [appliedReferralCode, setAppliedReferralCode] = useState(null);

  const {
    combined_split_buys = [],
    split_buyer_address = null,
    payment_url = null,
    cost_breakdown = [],
  } = checkoutData || {};

  const displayName = useMemo(() => {
    if (user?.firstname || user?.lastname) {
      return `${user.firstname ?? ""} ${user.lastname ?? ""}`.trim();
    }
    return selectedAddress?.name || "Not specified";
  }, [user, selectedAddress]);
  const toggleAddressModal = ()=>{
    if(isAuthenticated){
      toggleAuthenticatedUserAddressModal()
    }else if(isGuestUser){
      navigate("/guest-sign-in")
    }
  }
  const buildSplitBuyPayload = useCallback((items) => {
    return (items || []).flat().map((it) => ({
      id: it.split_buy_id || it.id,
      number_of_slots_to_be_purchased: it.number_of_slots_purchased || 1,
    }));
  }, []);

  // Handlers
  const handleAddressSelect = useCallback(async (address) => {
    if (loading || isGuestUser) return;

    setSelectedAddress(address);
    setIsAddressModalOpen(false);
    Mixpanel.alias("Tap-DeliveryAddressScreen-AddressSelected");

    try {
      setLoading(true);
      const splitBuys = buildSplitBuyPayload(combined_split_buys);

      if (!splitBuys.length) {
        activateNotification({ title: "Error", body: "No items to recombine." });
        return;
      }

      const result = await handleCheckout(splitBuys, address?._id);
      activateNotification({ title: result.success ? "Success" : "Error", body: result.message });

      if (result.success) {
        const newCheckoutData = result.data?.data || result.data || result;
        setCheckoutData(newCheckoutData);
      } else if (result.message?.includes("Please add a delivery")) {
        setIsAddressModalOpen(true);
      }
    } catch (e) {
      activateNotification({ title: "Error", body: "Failed to re-combine delivery." });
    } finally {
      setLoading(false);
    }
  }, [loading, combined_split_buys, buildSplitBuyPayload, handleCheckout, setIsAddressModalOpen]);

  const toggleReferralModal = useCallback(() => {
    setIsReferralModalOpen(prev => !prev);
  }, []);

  const handleApplyReferralCode = useCallback(async (code) => {
    try {
      setLoading(true);
      const splitBuys = buildSplitBuyPayload(combined_split_buys);

      if (!splitBuys.length) {
        activateNotification({ title: "Error", body: "No items to apply referral code." });
        throw new Error("No items available");
      }

      const addressId = split_buyer_address?._id || selectedAddress?._id;
      const result = await handleCheckout(splitBuys, addressId, code);

      if (result.success) {
        setAppliedReferralCode(code);
        const newCheckoutData = result.data?.data || result.data || result;
        setCheckoutData(newCheckoutData);
        activateNotification({ title: "Success", body: "Referral code applied successfully!" });
        return Promise.resolve();
      } else {
        activateNotification({ title: "Error", body: result.message || "Failed to apply referral code" });
        throw new Error(result.message || "Failed to apply referral code");
      }
    } catch (error) {
      activateNotification({ title: "Error", body: error.message || "Failed to apply referral code" });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [combined_split_buys, buildSplitBuyPayload, handleCheckout, split_buyer_address, selectedAddress]);

  const handleMakePayment = useCallback(() => {
    if (!payment_url) {
      activateNotification({ title: "Error", body: "Payment URL unavailable." });
      return;
    }
    const cartItemIds = cartItems.map(item => item._id);
    sessionStorage.setItem("pending_payment_cart_items", JSON.stringify(cartItemIds));
    window.location.href = payment_url;
  }, [payment_url, cartItems]);

  // Effects
  useEffect(() => {
    if (!location.state) {
      navigate("/cart", { replace: true });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    Mixpanel.alias("View-CheckoutScreen");
  }, []);

  if (!checkoutData) return <PageLoader />;

  // Helper components
  const HeaderSection = ({ title, onBack }) => (
      <div className="first_content align-items-center my-3 justify-content-between my-3 header">
        <div className="close-btn tw-cursor-pointer" onClick={onBack}>
          <IoIosArrowBack size={25} />
        </div>
        <h4 className="inherit-all !tw-flex-1 d-flex justify-content-center m-0">
          {title}
        </h4>
      </div>
  );

  const SectionHeader = ({ title }) => (
      <>
        <div className="first_content title my-md-3 header">{title}</div>
        <div className="cart-icons-line md-hidden" />
      </>
  );

  const DeliveryInfoSection = () => (
      <GrayCardWrapper className="border-1 tw-space-y-2 cart-delivery-info-pad cart-delivery-information">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="tw-space-y-2">
            <h5>Name: <span>{displayName}</span></h5>
            <h5>Email: <span className="text-lowercase">{user?.email || "Not specified"}</span></h5>
            <h5>Phone: <span>{user?.phone || user?.phonenumber || "Not specified"}</span></h5>
            <h5>Address: <span>{split_buyer_address?.street || selectedAddress?.street ||  selectedAddress?.address || "Not specified"}</span></h5>
          </div>
              <ChangeAddressButton onClick={toggleAddressModal} />
        </div>
      </GrayCardWrapper>
  );

  const ChangeAddressButton = ({ onClick }) => (
      <Button
          color="link"
          size="sm"
          onClick={onClick}
          className="p-0 tw-font-bold text-decoration-none"
      >
        Change
      </Button>
  );

  const DeliveryGroup = ({ group, groupIndex, totalGroups }) => {
    const firstItem = group[0];
    const deliveryDate = firstItem.delivery_date;
    const state = firstItem.state;

    return (
        <div key={groupIndex} className="delivery-group">
          <div className="delivery-group-header mb-3 p-3 bg-light rounded">
            <h5 className="mb-1">
              Delivery to {state} on {deliveryDate ? CustomHelper.formatDateToReadable(new Date(deliveryDate)) : "Not specified"}
            </h5>
            <p className="text-muted mb-0">
              {group.length} item{group.length > 1 ? "s" : ""} in this delivery
            </p>
          </div>
          <DeliveryStepper group={group} isLastStep={groupIndex === totalGroups-1} />
        </div>
    );
  };

  const DeliveryStepper = ({ group, isLastStep }) => {
    const names = group?.map(item => item.split_buy_name) || [];
    const displayName = names.length === 1 ? names[0] :
        names.length === 2 ? `${names[0]} & ${names[1]}` :
            `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;

    return (
        <ol className="c-stepper mb-3">
          <li className={`c-stepper__item ${isLastStep ? '!tw-pb-0' : ''}`}>
            <div className="c-stepper__dot"><div /></div>
            <div className="c-stepper__content">
              <div className="d-flex align-items-center justify-content-between">
                <h4 className="title text-capitalize">{displayName}</h4>
                <h4 className="title">
                  <NumericFormat
                      value={group?.[0]?.delivery_fee ?? 0}
                      displayType="text"
                      thousandSeparator=","
                      decimalScale={2}
                      fixedDecimalScale
                      prefix="₦"
                  />
                </h4>
              </div>
              <CardText className="card-location flex-nowrap d-flex align-items-center">
                <b>Delivery: {group?.[0]?.delivery_date ? CustomHelper.formatDateToReadable(new Date(group[0].delivery_date)) : "Not specified"}</b>
              </CardText>
            </div>
          </li>
        </ol>
    );
  };

  const CostBreakdownItem = ({ item }) => (
      <div className={`d-flex mb-2 align-items-center justify-content-between ${item.label.toLowerCase().includes("total") ? "total" : ""}`}>
        <h4 className="title simmer_down">{item.label}:</h4>
        <h5 className={`subtitle ${item.value < 0 ? "custom_app_discount" : ""}`}>
          <NumericFormat
              value={item.value ?? 0}
              displayType="text"
              thousandSeparator=","
              decimalScale={2}
              fixedDecimalScale
              prefix="₦"
          />
        </h5>
      </div>
  );

  const OrderSummaryItem = ({ item }) => (
      <>
        {item.label.toLowerCase().includes("total payment") && <div className="cart-icons-line" />}
        <div className={`first_content d-flex justify-content-between mb-md-3 mb-2 ${item.label.toLowerCase().includes("total payment") ? "header total" : "sub-total"}`}>
          <h4 className="title">{item.label}:</h4>
          <h6>
            <NumericFormat
                value={item.value ?? 0}
                displayType="text"
                thousandSeparator=","
                decimalScale={2}
                fixedDecimalScale
                prefix="₦"
            />
          </h6>
        </div>
      </>
  );

  const DiscountCodeSection = () => (
      <GrayCardWrapper className="border-1 tw-space-y-2 cart-delivery-info-pad cart-delivery-information">
        <div onClick={toggleReferralModal} className="tw-cursor-pointer d-flex align-items-center justify-content-between flex-nowrap">
          <CardText className="card-location flex-nowrap d-flex align-items-center">
            <h5 className="tw-whitespace-break-spaces">Discount Code</h5>
            <CiCircleInfo style={{ transform: "rotate(180deg)" }} color="#F0B149" strokeWidth={1} />
          </CardText>
          <CardText className="card-location flex-nowrap d-flex align-items-center">
          <span className="simmer_down tw-text-xs tw-whitespace-break-spaces">
            {appliedReferralCode ? "Change Code" : "Use Code"}
          </span>
            <RiArrowDropRightLine color="#CCCCCC" />
          </CardText>
        </div>
      </GrayCardWrapper>
  );

  return (
      <Wrapper>
        <Container>
          <HeaderSection title="Review And Checkout" onBack={() => navigate(-1)} />

          <Row xl={12}>
            <Col className="d-flex flex-column row-gap-md-0 row-gap-2" md={7}>
              <div>
                <SectionHeader title="Delivery Information" />
                <DeliveryInfoSection />
              </div>

              <div>
                <SectionHeader title="Combined Delivery Summary" />
                <div className="cart-delivery-info-pad border-1 cart-deliver-summary">
                  <div className="d-flex delivery-address gap-2 mb-3 align-items-start">
                    <img src={deliveryIcon} width={31} height={31} alt="delivery icon" />
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center mb-md-2 mb-0">
                        <h4 className="title mb-0">Delivery Address</h4>
                        <ChangeAddressButton onClick={toggleAddressModal} />
                      </div>
                      <CardText className="card-location flex-nowrap d-flex align-items-center">
                        <IoLocationOutline className="me-1 me-md-2" />
                        {split_buyer_address?.street || selectedAddress?.street || selectedAddress?.address || "Not specified"}
                      </CardText>
                    </div>
                  </div>

                  {combined_split_buys?.map((group, groupIndex) => (
                      <DeliveryGroup key={groupIndex} group={group}
                                     totalGroups={combined_split_buys.length}
                                     groupIndex={groupIndex} />
                  ))}

                  <div className="amt-summary">
                    {cost_breakdown?.filter(item => item?.value !== 0)?.map((cb) => (
                        <CostBreakdownItem key={cb.label} item={cb} />
                    ))}
                  </div>
                </div>
              </div>
            </Col>

            <Col className="d-flex flex-column row-gap-md-0 row-gap-2" md={5}>
              <div>
                <SectionHeader title="My Order" />
                <div>
                  {combined_split_buys?.flat()?.map((item, idx) => (
                      <CartItemCardComponent
                          key={idx}
                          title={item.split_buy_name}
                          location={item.state}
                          amount={(item.buy_in_amount ?? 0).toLocaleString()}
                          splits={item.number_of_slots_purchased}
                          image={item.split_buy_image_url}
                          deliveryDate={item.delivery_date}
                          join_period_end_date={item.split_buy_delivery_date}
                          left={false}
                      />
                  ))}
                </div>
              </div>

              {!isGuestUser && (
                  <>
                    <DiscountCodeSection />
                    <div className="cart-icons-line" />
                  </>
              )}
              <OrderSummaryStyle>
                {cost_breakdown?.filter(item => item?.value !== 0)?.map((item) => (
                    <OrderSummaryItem key={item.label} item={item} />
                ))}

                <div className="cart-icons-line" />
                <div className="mobile-sticky-checkout">
                  <AuthCustomModalDescriptionStyleB className="my-md-4 my-2">
                    By proceeding, you agree to our{" "}
                    <a href="/terms-and-conditions">Terms of Use</a> and{" "}
                    <a href="/privacy-policy">Privacy Policy</a>
                  </AuthCustomModalDescriptionStyleB>

                  <Button
                      className="mb-md-3 mb-0 w-100"
                      onClick={handleMakePayment}
                      disabled={!payment_url}
                  >
                    {payment_url ? "Make Payment" : "Payment Unavailable"}
                  </Button>
                </div>
              </OrderSummaryStyle>
            </Col>
          </Row>
        </Container>

        <AddressManagementModalComponent
            onSelect={handleAddressSelect}
            view="list"
            isOpen={isAddressModalOpen}
            toggle={toggleAddressModal}
        />

        {loading && <LoadingOverlay />}

        <AddDiscountCodeModalSection
            isOpen={isReferralModalOpen}
            toggle={toggleReferralModal}
            onApplyCode={handleApplyReferralCode}
        />
      </Wrapper>
  );
});

